/**
 * Holder-wide (cross-account) email settings — the client side of the
 * server-stored `email_user_settings` blob (message-list preview length, mobile
 * swipe gestures, and the Scheduled/Snooze feature opt-ins).
 *
 * Replaces the old per-device localStorage + per-account `modules` storage:
 * these preferences now follow the user across every connected account and every
 * device. `useEmailListPrefs` (preview length) and `useSwipePrefs` (swipe) are
 * thin adapters over this hook; the feature toggles are read directly.
 *
 * The query resolves to the defaults until the fetch lands (`placeholderData`),
 * so consumers always have a value and never flash. Writes send the whole blob
 * (last-write-wins) and update optimistically.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type EmailUserSettingsDto } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** Bounds for the undo-send window (seconds). */
export const EMAIL_UNDO_SEND_MIN_SECONDS = 5;
export const EMAIL_UNDO_SEND_MAX_SECONDS = 30;

export const DEFAULT_EMAIL_USER_SETTINGS: EmailUserSettingsDto = {
  previewLines: 1,
  swipe: { enabled: true, left: 'trash', right: 'archive' },
  features: { scheduleEnabled: false, snoozeEnabled: false },
  undoSend: { enabled: false, seconds: 10 },
  autoSignature: false,
  replyQuoteOrder: 'above',
  conversationThreadingEnabled: false,
  inviteCalendarId: null,
  trustContacts: true,
  blockAcrossMailboxes: false,
};

/**
 * The Holder's global email settings. Always returns a value — the defaults
 * until the server fetch resolves — so callers render without a loading flash.
 */
export function useEmailUserSettings(): EmailUserSettingsDto {
  const { data } = useQuery({
    queryKey: queryKeys.email.userSettings(),
    queryFn: () => api.getEmailMailboxUserSettings().then((r) => r.settings),
    staleTime: 5 * 60 * 1000,
    placeholderData: DEFAULT_EMAIL_USER_SETTINGS,
  });
  return data ?? DEFAULT_EMAIL_USER_SETTINGS;
}

/**
 * Persist the Holder's global email settings wholesale (last-write-wins), with
 * an optimistic cache update so the UI reflects the change immediately.
 */
export function useSetEmailUserSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings: EmailUserSettingsDto) =>
      api.setEmailMailboxUserSettings(settings).then((r) => r.settings),
    onMutate: async (settings) => {
      await qc.cancelQueries({ queryKey: queryKeys.email.userSettings() });
      const prev = qc.getQueryData<EmailUserSettingsDto>(queryKeys.email.userSettings());
      qc.setQueryData(queryKeys.email.userSettings(), settings);
      return { prev };
    },
    onError: (_err, _settings, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKeys.email.userSettings(), ctx.prev);
    },
    onSuccess: (settings) => qc.setQueryData(queryKeys.email.userSettings(), settings),
  });
}
